import type { Dayjs } from 'dayjs';
import { DiscountCodeBase } from '../../../contracts/discount-code.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Price } from '../price.js';
import { toEnumFromString } from '../../../serialization/enum-utils.js';
import { GiftCard, GiftCardStatus } from './gift-card.js';
export { GiftCardStatus } from './gift-card.js';

export class GiftCardDetails extends DataModel implements DiscountCodeBase {
  static readonly TYPE_ID = '3e4d714a142c47ee88c123d28428a206';
  static readonly SCHEMA_VERSION = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly code: string,
    public readonly creationDate: Dayjs,
    public readonly expirationDate: Dayjs,
    public readonly status: GiftCardStatus,
    public readonly remainingValue: Price,
  ) {
    super();
  }

  get $typeId(): string {return GiftCardDetails.TYPE_ID;}
  get $schemaVersion(): VersionRange {return VersionRange.exactly(GiftCardDetails.SCHEMA_VERSION);}

  static verifyType(value: DataModel): boolean {return value instanceof GiftCardDetails;}
  static assertType(value: DataModel): value is GiftCardDetails {return value instanceof GiftCardDetails;}

  static create(args: {
    uniqueId: string;
    businessRef: BusinessModuleRef;
    code: string;
    creationDate: Dayjs;
    expirationDate: Dayjs;
    status: GiftCardStatus;
    remainingValue: Price;
  }): GiftCardDetails {
    return new GiftCardDetails(
        args.uniqueId,
        args.businessRef.clone(),
        args.code,
        args.creationDate.clone(),
        args.expirationDate.clone(),
        args.status,
        args.remainingValue.clone(),
    );
  }

  static fromGiftCard(card: GiftCard): GiftCardDetails {
    if(card.businessRef == null) {
      throw new Error('Global gift cards are not supported by checkout.');
    }
    return this.create({ ...card, businessRef: card.businessRef, remainingValue: card.remainingValue });
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardDetails {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      code: MapUtils.getString(map, 'code'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      expirationDate: context.decodeDate(MapUtils.getNumber(map, 'expirationDate')),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GiftCardStatus),
      remainingValue: Price.decode(MapUtils.getString(map, 'remainingValue')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      businessRef: this.businessRef.encode(),
      code: this.code,
      creationDate: context.encodeDate(this.creationDate),
      expirationDate: context.encodeDate(this.expirationDate),
      $status: this.status,
      remainingValue: this.remainingValue.encode(),
    };
  }

  copyWith(args: Partial<GiftCardDetails> = {}): this {
    return GiftCardDetails.create({ ...this, ...args }) as this;
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    return this.uniqueId === other.uniqueId && this.code === other.code &&
      isSameData(this.businessRef, other.businessRef) && this.status === other.status &&
      this.creationDate.isSame(other.creationDate) && this.expirationDate.isSame(other.expirationDate) &&
      isSameData(this.remainingValue, other.remainingValue);
  }
}

export class GiftCardDetailsBuilder extends DataModelBuilder<GiftCardDetails> {
  build(context: SerializationContext, map: SerializedData): GiftCardDetails {
    return GiftCardDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(_context: SerializationContext, map: SerializedData): SerializedData | null {
    return MapUtils.getVersionRange(map).contains(GiftCardDetails.SCHEMA_VERSION) ? map : null;
  }
}
